import { HttpService, createHttpService } from '@/services/HttpService';
import {
  Contact,
  ContactType,
  areContactRequiredFieldsFilled,
  isCompanyBillingContact,
  isContactAdded,
  isLegalContact
} from '@/app/domain/Contact';
import { createUrlSearchParams } from '@/app/library/createUrlSearchParams';
import { getValue } from '@/app/library/getValue';
import { getCountry } from '@/app/library/country-state-picker';
import { CompanyIndustry, CompanySize } from '../domain/Company';

export interface CreateContactPayload {
  type: ContactType[];
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
}

export interface EditContactPayload {
  id: string;
  type: ContactType[];
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  jobTitle?: string;
}

export interface EditCompanyContactPayload {
  id: string;
  type: ContactType[];
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  country?: string;
  state?: string;
  city?: string;
  zipCode?: string;
  address?: string;
  industry?: CompanyIndustry;
  size?: CompanySize;
  website?: string;
}

export interface DeleteContactPayload {
  id: string;
}

export type SendVerificationEmailPayload = {
  userId: string;
  email: string;
};

enum ServerContactType {
  Legal = 'legal',
  Primary = 'primary',
  Management = 'management',
  Technical = 'technical',
  Billing = 'billing',
  CompanyBilling = 'company_billing'
}
export type ServerContact = {
  id: string;
  created_at: string;
  updated_at: string;
  tenant_id: string;
  user_id: string | null;
  types: ServerContactType[];
  email: string;
  address1: string;
  address2: string;
  country: string;
  state: string;
  city: string;
  zipcode: string;
  phone: string;
  firstname: string;
  lastname: string;
  title: string;
  website: string;
  industry: string;
  organization_size: string;
  email_confirmed: boolean;
  aan: string;
  language: string;
  fax: string;
};
export type ServerUser = {
  id: string;
  contact: ServerContact;
  version: number;
  login: string;
};

type TenantContactsResponse = {
  items: string[];
};
type TenantUsersResponse = {
  items: string[];
};

type ContactsResponse = {
  items: ServerContact[];
};
type UsersResponse = {
  items: ServerUser[];
};

type UserResponse = {
  id: string;
  version: number;
};

type CreateContactData = {
  tenant_id: string;
  types: ServerContactType[];
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  title?: string;
};
type CreateContactResponse = {};

type PutContactData = {
  id: string;
  types: ServerContactType[];
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  title?: string;
};

type PutCompanyContactData = {
  id: string;
  types: ServerContactType[];
  firstname: string;
  lastname: string;
  email: string;
  phone?: string;
  country?: string;
  state?: string;
  city?: string;
  zipcode?: string;
  address1?: string;
  website?: string;
  industry?: string;
  organization_size?: string;
};

type PutContactResponse = {};

type DeleteContactResponse = {};

type SendVerificationEmailData = {
  version: number;
  contact: {
    email: string;
  }
};
type SendVerificationEmailResponse = {};

export class ContactService {
  constructor(
    private tenantUuid: string,
    private httpService: HttpService
  ) {

  }

  public async getContactList(): Promise<Contact[]> {
    const [tenantContactList, userContactList]: Contact[][] = await Promise.all([
      this.getFilteredTenantContactList(),
      this.getUserContactList()
    ]);
    return [
      ...tenantContactList,
      ...userContactList
    ];
  }

  public async createContact(payload: CreateContactPayload): Promise<void> {
    try {
      await this.httpService.post<CreateContactData, CreateContactResponse>(
        '/api/2/contacts',
        this.convertCreateContactPayloadToPostContactData(payload)
      );
    } catch (error) {

    }
  }

  public async editContact(payload: EditContactPayload): Promise<void> {
    try {
      await this.httpService.put<PutContactData, PutContactResponse>(
        `/api/2/contacts/${payload.id}`,
        this.convertEditContactPayloadToPutContactData(payload)
      );
    } catch (error) {

    }
  }

  public async editCompanyContact(payload: EditCompanyContactPayload): Promise<void> {
    try {
      await this.httpService.put<PutCompanyContactData, PutContactResponse>(
        `/api/2/contacts/${payload.id}`,
        this.convertEditCompanyContactPayloadToPutContactData(payload)
      );
    } catch (error) {

    }
  }

  public async deleteContact(payload: DeleteContactPayload): Promise<void> {
    try {
      await this.httpService.delete<void, DeleteContactResponse>(
        `/api/2/contacts/${payload.id}`
      );
    } catch (error) {

    }
  }

  public async sendVerificationEmail(payload: SendVerificationEmailPayload): Promise<void> {
    try {
      const userResponse: UserResponse = await this.httpService.get<void, UserResponse>(
        `/api/2/users/${payload.userId}`
      );
      await this.httpService.put<SendVerificationEmailData, SendVerificationEmailResponse>(
        `/api/2/users/${payload.userId}`,
        {
          contact: {
            email: payload.email
          },
          version: userResponse.version
        }
      );
    } catch (error) {

    }
  }

  private async getFilteredTenantContactList(): Promise<Contact[]> {
    try {
      const tenantContactsResponse: TenantContactsResponse = await this.httpService.get<void, TenantContactsResponse>(
        `/api/2/tenants/${this.tenantUuid}/contacts`
      );

      if (tenantContactsResponse.items.length) {
        const contactsResponse: ContactsResponse = await this.httpService.get<void, ContactsResponse>(
          '/api/2/contacts',
          { params: createUrlSearchParams({ uuids: tenantContactsResponse.items.join(',') }) }
        );
        return this.filterTenantContactList(
          this.convertServerContactsToContacts(contactsResponse.items)
        );
      }
      return [];
    } catch (error) {
      return [];
    }
  }

  public async getFullTenantContactList(): Promise<Contact[]> {
    try {
      const tenantContactsResponse: TenantContactsResponse = await this.httpService.get<void, TenantContactsResponse>(
        `/api/2/tenants/${this.tenantUuid}/contacts`
      );

      if (tenantContactsResponse.items.length) {
        const contactsResponse: ContactsResponse = await this.httpService.get<void, ContactsResponse>(
          '/api/2/contacts',
          { params: createUrlSearchParams({ uuids: tenantContactsResponse.items.join(',') }) }
        );
        return this.convertServerContactsToContacts(contactsResponse.items);
      }
      return [];
    } catch (error) {
      return [];
    }
  }

  private async getUserContactList(): Promise<Contact[]> {
    try {
      const tenantUsersResponse: TenantUsersResponse = await this.httpService.get<void, TenantUsersResponse>(
        `/api/2/tenants/${this.tenantUuid}/users`
      );

      if (tenantUsersResponse.items.length) {
        const usersResponse: UsersResponse = await this.httpService.get<void, UsersResponse>(
          '/api/2/users',
          { params: createUrlSearchParams({ uuids: tenantUsersResponse.items.join(',') }) }
        );
        return this.filterUserContactList(
          this.convertServerContactsToContacts(
            this.extractServerContactsFromServerUsers(usersResponse.items)
          )
        );
      }
      return [];
    } catch (error) {
      return [];
    }
  }

  private filterTenantContactList(contactList: Contact[]): Contact[] {
    return contactList.filter((contact: Contact) => (
      areContactRequiredFieldsFilled(contact)
      && isContactAdded(contact)
      && !isLegalContact(contact)
      && !isCompanyBillingContact(contact)
    ));
  }

  private filterUserContactList(contactList: Contact[]): Contact[] {
    return contactList.filter((contact: Contact) => (
      areContactRequiredFieldsFilled(contact)
      && !isContactAdded(contact)
    ));
  }

  private convertServerContactsToContacts(serverContacts: ServerContact[]): Contact[] {
    return serverContacts.map((serverContact: ServerContact): Contact => ({
      id: serverContact.id,
      type: this.convertServerContactTypeToContactType(serverContact.types || []),
      firstName: serverContact.firstname || '',
      lastName: serverContact.lastname || '',
      email: serverContact.email || '',
      phone: serverContact.phone || '',
      jobTitle: serverContact.title || '',
      userId: serverContact.user_id,
      address: serverContact.address1 || '',
      city: serverContact.city || '',
      state: serverContact.state || '',
      country: serverContact.country ? getCountry(serverContact.country)?.code || '' : '',
      zipCode: serverContact.zipcode || ''
    }));
  }

  private extractServerContactsFromServerUsers(serverUsers: ServerUser[]): ServerContact[] {
    return serverUsers.map((serverUser: ServerUser): ServerContact => ({
      user_id: serverUser.id,
      ...serverUser.contact
    }));
  }

  private convertCreateContactPayloadToPostContactData(payload: CreateContactPayload): CreateContactData {
    return {
      tenant_id: this.tenantUuid,
      types: this.convertContactTypeToServerContactType(payload.type),
      firstname: payload.firstName,
      lastname: payload.lastName,
      email: payload.email,
      phone: payload.phone
        ? getValue(payload.phone, { prefix: '+' })
        : payload.phone,
      title: payload.jobTitle
    };
  }

  private convertEditContactPayloadToPutContactData(payload: EditContactPayload): PutContactData {
    return {
      id: payload.id,
      types: this.convertContactTypeToServerContactType(payload.type),
      firstname: payload.firstName,
      lastname: payload.lastName,
      email: payload.email,
      phone: payload.phone
        ? getValue(payload.phone, { prefix: '+' })
        : payload.phone,
      title: payload.jobTitle
    };
  }

  private convertEditCompanyContactPayloadToPutContactData(payload: EditCompanyContactPayload): PutCompanyContactData {
    return {
      id: payload.id,
      types: this.convertContactTypeToServerContactType(payload.type),
      firstname: payload.firstName,
      lastname: payload.lastName,
      email: payload.email,
      phone: payload.phone
        ? getValue(payload.phone, { prefix: '+' })
        : payload.phone,
      country: payload.country?.toUpperCase(),
      state: payload.state,
      city: payload.city,
      zipcode: payload.zipCode,
      address1: payload.address,
      industry: payload.industry,
      organization_size: payload.size,
      website: payload.website
    };
  }

  private convertServerContactTypeToContactType(serverContactType: ServerContactType[]): ContactType[] {
    const contactTypeMap = {
      [ServerContactType.Legal]: ContactType.Legal,
      [ServerContactType.Primary]: ContactType.Primary,
      [ServerContactType.Management]: ContactType.Business,
      [ServerContactType.Technical]: ContactType.Technical,
      [ServerContactType.Billing]: ContactType.Billing,
      [ServerContactType.CompanyBilling]: ContactType.CompanyBilling
    };
    return serverContactType.map((type: ServerContactType): ContactType => contactTypeMap[type]);
  }

  private convertContactTypeToServerContactType(contactType: ContactType[]): ServerContactType[] {
    const contactTypeMap = {
      [ContactType.Legal]: ServerContactType.Legal,
      [ContactType.Primary]: ServerContactType.Primary,
      [ContactType.Business]: ServerContactType.Management,
      [ContactType.Technical]: ServerContactType.Technical,
      [ContactType.Billing]: ServerContactType.Billing,
      [ContactType.CompanyBilling]: ServerContactType.CompanyBilling
    };
    return contactType.map((type: ContactType): ServerContactType => contactTypeMap[type]);
  }
}

export function createContactService(selfTenantUuid: string, tenantUuid: string): ContactService {
  const httpService: HttpService = createHttpService(selfTenantUuid);

  return new ContactService(tenantUuid, httpService);
}
